import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import axiosAdminInstance from "../../api/adminAxios";

interface Subscription {
    id: number;
    name: string;
    price_monthly: number;
    price_yearly: number;
    stripe_amount_id_month: string;
    stripe_amount_id_year: string;
    features: string[];
    discount_percent: number;
}

export default function ManageSubscriptions() {
    const [plans, setPlans] = useState<Subscription[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState<number | null>(null);
    const [newPlan, setNewPlan] = useState<any>({
        name: "",
        price_monthly: "",
        price_yearly: "",
        stripe_amount_id_month: "",
        stripe_amount_id_year: "",
        discount_percent: "",
        features: "",
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            const res = await axiosAdminInstance.get<Subscription[]>("/admin/subscriptions");
            setPlans(res.data);
        } catch (error) {
            console.error("❌ Failed to load subscriptions:", error);
        }
    };

    const handleSubmit = async () => {
        try {
            const body = {
                ...newPlan,
                discount_percent: parseFloat(newPlan.discount_percent),
                features: newPlan.features.split(",").map((f: string) => f.trim()),
            };

            const endpoint = isEditing
                ? `/admin/update_subscriptions/${currentId}`
                : `/admin/add_subscriptions`;

            const method = isEditing ? "put" : "post";

            await axiosAdminInstance[method](endpoint, body);

            setShowModal(false);
            resetForm();
            fetchSubscriptions();
        } catch (err) {
            console.error("❌ Submit failed:", err);
        }
    };

    const handleEdit = (plan: Subscription) => {
        setNewPlan({
            ...plan,
            features: plan.features.join(", "),
        });
        setCurrentId(plan.id);
        setIsEditing(true);
        setShowModal(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this plan?")) return;

        try {
            await axiosAdminInstance.delete(`/admin/delete_subscriptions/${id}`);
            fetchSubscriptions();
        } catch (err) {
            console.error("❌ Delete failed:", err);
        }
    };

    const resetForm = () => {
        setNewPlan({
            name: "",
            price_monthly: "",
            price_yearly: "",
            stripe_amount_id_month: "",
            stripe_amount_id_year: "",
            discount_percent: "",
            features: "",
        });
        setIsEditing(false);
        setCurrentId(null);
    };

    return (
        <div className="min-h-screen flex">
            <AdminSidebar />

            <div className="flex-1 p-6 bg-gray-100">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">💳 Manage Subscriptions</h2>
                    <button
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        ➕ Add Plan
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan) => (
                        <div key={plan.id} className="border p-4 rounded shadow bg-white relative">
                            <h3 className="text-lg font-bold">{plan.name}</h3>
                            <p className="text-sm text-gray-600">
                                ${plan.price_monthly}/mo | ${plan.price_yearly}/yr
                            </p>
                            <p className="text-sm text-green-600">
                                🎁 {plan.discount_percent}% yearly discount
                            </p>
                            <ul className="mt-2 text-sm text-gray-500 list-disc list-inside">
                                {plan.features.map((f, idx) => (
                                    <li key={idx}>{f}</li>
                                ))}
                            </ul>
                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => handleEdit(plan)}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    ✏️ Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(plan.id)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    🗑️ Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-full max-w-lg shadow-lg">
                        <h3 className="text-lg font-bold mb-4">
                            {isEditing ? "✏️ Edit Plan" : "➕ Create New Plan"}
                        </h3>
                        <div className="space-y-3">
                            {["name", "price_monthly", "price_yearly", "stripe_amount_id_month", "stripe_amount_id_year", "discount_percent"].map((field) => (
                                <input
                                    key={field}
                                    type={field.includes("price") || field.includes("discount") ? "number" : "text"}
                                    placeholder={field.replaceAll("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                                    value={newPlan[field]}
                                    onChange={(e) =>
                                        setNewPlan({ ...newPlan, [field]: e.target.value })
                                    }
                                    className="w-full p-2 border rounded"
                                />
                            ))}

                            <textarea
                                placeholder="Features (comma separated)"
                                value={newPlan.features}
                                onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })}
                                className="w-full p-2 border rounded"
                            />
                        </div>

                        <div className="flex justify-end mt-4 space-x-3">
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    resetForm();
                                }}
                                className="px-4 py-2 rounded bg-gray-300 text-gray-800 hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                            >
                                {isEditing ? "Update" : "Create"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
